import { useState } from "react";
import { toast } from "sonner";
import { Loader2, PackageCheck, Plus, Trash2, Upload, CheckCircle2, Link2, KeyRound, FileText } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { useVendorDeliveries, useVendorRefresh } from "@/hooks/useVendor";
import { formatPrice, formatDate } from "@/lib/format";
import { AdminModal, Field, Pill, inputCls } from "@/pages/admin/ui";

const TYPES = [
  { value: "link", label: "Lien", icon: Link2 },
  { value: "key", label: "Clé / Code", icon: KeyRound },
  { value: "text", label: "Texte", icon: FileText },
  { value: "file", label: "Fichier", icon: Upload },
];

const EMPTY_ITEM = { type: "link", title: "", url: "", value: "", content: "", file_path: "", file_name: "" };

export default function VendorDeliveries() {
  const { data, isLoading } = useVendorDeliveries();
  const refresh = useVendorRefresh();
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);
  const [items, setItems] = useState([]);
  const [saving, setSaving] = useState(false);
  const [busy, setBusy] = useState(false);

  const orders = data?.items || [];

  const openDetail = async (o) => {
    setSelected(o);
    try {
      const { data: d } = await api.get(`/vendor/deliveries/${o.id}`);
      setDetail(d);
      setItems((d.digital_delivery?.items || []).map((i) => ({ ...EMPTY_ITEM, ...i })));
    } catch (e) { toast.error(apiError(e)); setSelected(null); }
  };

  const addItem = () => setItems((s) => [...s, { ...EMPTY_ITEM, id: "" }]);
  const setItem = (i, k, v) => setItems((s) => s.map((x, j) => (j === i ? { ...x, [k]: v } : x)));

  const uploadFile = async (i, file) => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data: res } = await api.post(`/vendor/deliveries/${selected.id}/upload`, fd,
        { headers: { "Content-Type": "multipart/form-data" } });
      setItem(i, "file_path", res.path); setItem(i, "file_name", res.name);
      if (!items[i].title) setItem(i, "title", res.name);
      toast.success("Fichier téléversé");
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/vendor/deliveries/${selected.id}/content`, {
        items: items.filter((i) => i.title.trim()),
      });
      toast.success("Livraison enregistrée");
      refresh(["vendor-deliveries", "vendor-badges"]);
      openDetail(selected);
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };

  const validate = async () => {
    if (!window.confirm("Valider et libérer la livraison au client ? Le paiement sera marqué comme confirmé.")) return;
    setSaving(true);
    try {
      await api.post(`/vendor/deliveries/${selected.id}/validate`);
      toast.success("Livraison validée — le client a été notifié");
      setSelected(null); setDetail(null);
      refresh(["vendor-deliveries", "vendor-badges", "vendor-orders", "vendor-dashboard"]);
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };

  return (
    <div className="space-y-5" data-testid="vendor-deliveries">
      <div>
        <h1 className="font-display text-2xl font-extrabold text-ink">Livraisons numériques</h1>
        <p className="mt-1 text-sm text-slate-500">Préparez et libérez les contenus numériques de vos ventes.</p>
      </div>

      {isLoading ? (
        <div className="flex h-40 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-brand" /></div>
      ) : !orders.length ? (
        <div className="rounded-2xl border border-dashed border-line bg-white p-10 text-center">
          <PackageCheck className="mx-auto h-9 w-9 text-slate-300" />
          <p className="mt-3 text-sm text-slate-500">Aucune vente à livrer pour le moment.</p>
        </div>
      ) : (
        <div className="overflow-x-auto rounded-2xl border border-line bg-white">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-line text-left text-[11px] uppercase tracking-wide text-slate-400">
                <th className="p-4">Commande</th><th className="p-4">Client</th><th className="p-4">Contenu</th>
                <th className="p-4">Total</th><th className="p-4">Livraison</th><th className="p-4 text-right">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-line">
              {orders.map((o) => {
                const released = o.delivery?.status === "released";
                return (
                  <tr key={o.id} data-testid={`vdelivery-${o.id}`}>
                    <td className="p-4">
                      <p className="font-bold text-ink">{o.order_number}</p>
                      <p className="text-xs text-slate-400">{formatDate(o.created_at)}</p>
                    </td>
                    <td className="p-4 text-slate-600">{o.customer?.name || "—"}</td>
                    <td className="p-4 text-xs text-slate-500">{o.items.map((i) => i.name).join(", ")}</td>
                    <td className="p-4 font-bold text-ink">{formatPrice(o.total)}</td>
                    <td className="p-4">
                      <Pill tone={released ? "green" : "amber"}>{released ? "Livrée" : "À préparer"}</Pill>
                    </td>
                    <td className="p-4 text-right">
                      <button onClick={() => openDetail(o)} data-testid={`vdelivery-open-${o.id}`}
                        className="rounded-full bg-brand px-4 py-2 text-xs font-bold text-white hover:bg-brand-dark">
                        {released ? "Voir" : "Préparer"}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <AdminModal open={!!selected} onOpenChange={() => { setSelected(null); setDetail(null); }}
        title={`Livraison — ${selected?.order_number || ""}`} wide>
        {!detail ? (
          <div className="flex h-32 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-brand" /></div>
        ) : (
          <div className="space-y-4">
            <div className="flex items-center justify-between rounded-xl bg-surface p-3 text-sm">
              <span className="text-slate-600">{detail.customer?.name} · {detail.customer?.email}</span>
              <Pill tone={detail.digital_delivery?.status === "released" ? "green" : "amber"}>
                {detail.digital_delivery?.status === "released" ? "Livrée" : "En préparation"}
              </Pill>
            </div>

            {items.map((item, i) => (
              <div key={i} className="space-y-2 rounded-xl border border-line p-3" data-testid={`delivery-item-${i}`}>
                <div className="flex gap-2">
                  <select value={item.type} onChange={(e) => setItem(i, "type", e.target.value)} className={`${inputCls} w-32`}>
                    {TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                  </select>
                  <input value={item.title} onChange={(e) => setItem(i, "title", e.target.value)}
                    placeholder="Titre de l'élément" className={inputCls} data-testid={`delivery-title-${i}`} />
                  <button onClick={() => setItems((s) => s.filter((_, j) => j !== i))}
                    className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg border border-line text-rose-500">
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
                {item.type === "link" && (
                  <input value={item.url} onChange={(e) => setItem(i, "url", e.target.value)}
                    placeholder="https://..." className={inputCls} data-testid={`delivery-url-${i}`} />
                )}
                {item.type === "key" && (
                  <input value={item.value} onChange={(e) => setItem(i, "value", e.target.value)}
                    placeholder="Clé / code / identifiants" className={inputCls} data-testid={`delivery-key-${i}`} />
                )}
                {item.type === "text" && (
                  <textarea rows={2} value={item.content} onChange={(e) => setItem(i, "content", e.target.value)}
                    placeholder="Instructions, message..." className={inputCls} style={{ height: "auto" }} />
                )}
                {item.type === "file" && (
                  <label className="flex cursor-pointer items-center gap-2 rounded-lg border border-dashed border-line px-3 py-2.5 text-xs font-bold text-slate-500 hover:border-brand hover:text-brand">
                    {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
                    {item.file_name || "Téléverser un fichier"}
                    <input type="file" className="hidden" onChange={(e) => uploadFile(i, e.target.files?.[0])} data-testid={`delivery-file-${i}`} />
                  </label>
                )}
              </div>
            ))}

            <button onClick={addItem} data-testid="delivery-add-item"
              className="flex items-center gap-2 rounded-lg border border-dashed border-line px-4 py-2.5 text-xs font-bold text-slate-500 hover:border-brand hover:text-brand">
              <Plus className="h-4 w-4" /> Ajouter un élément
            </button>

            <div className="flex justify-end gap-2 border-t border-line pt-4">
              <button onClick={save} disabled={saving} data-testid="delivery-save"
                className="rounded-full border border-line px-5 py-2.5 text-sm font-bold text-ink hover:border-brand disabled:opacity-60">
                Enregistrer
              </button>
              {detail.digital_delivery?.status !== "released" && (
                <button onClick={validate} disabled={saving} data-testid="delivery-validate"
                  className="flex items-center gap-2 rounded-full bg-emerald-600 px-6 py-2.5 text-sm font-bold text-white disabled:opacity-60">
                  {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />} Valider & livrer
                </button>
              )}
            </div>
          </div>
        )}
      </AdminModal>
    </div>
  );
}
